import React, { useEffect, useMemo, useState, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Box, Heading, useToast, Flex } from '@chakra-ui/react';
import GenericTable from '../components/GenericTable';
import { getReportingData } from '../actions/reportingActions';
import { fetchCompanies } from '../actions/companyActions';
import { io } from 'socket.io-client';
import CompanySelect from '../components/CompanySelect';
import { useFieldPrivilege } from '../hooks/useFieldPrivilege';

const base_url = process.env.REACT_APP_API_URL_ADMIN;

const AllReporting = () => {
  const dispatch = useDispatch();
  const toast = useToast();
  const { filterColumns } = useFieldPrivilege();

  const user = useSelector((state) => state.auth.user);
  const companies = useSelector((state) => state.company?.companies ?? []);
  const isSuperAdmin = user?.role === 'super_admin';

  const [companyId, setCompanyId] = useState('');
  const [pageIndex, setPageIndex] = useState(0);
  const [pageSize, setPageSize] = useState(10);

  const { reportingData, reportingTotal, loading, error } = useSelector((state) => state.reporting);

  const fetchParamsRef = useRef({ companyId, pageIndex, pageSize });
  fetchParamsRef.current = { companyId, pageIndex, pageSize };

  useEffect(() => {
    dispatch(fetchCompanies());
  }, [dispatch]);

  useEffect(() => {
    if (isSuperAdmin || !user?.companySlug || !companies.length) return;
    const match = companies.find((c) => c.slug === user.companySlug);
    if (match?._id) setCompanyId(String(match._id));
  }, [isSuperAdmin, user?.companySlug, companies]);

  useEffect(() => {
    setPageIndex(0);
  }, [companyId]);

  useEffect(() => {
    if (reportingTotal == null) return;
    const maxIndex = Math.max(0, Math.ceil(reportingTotal / pageSize) - 1);
    if (pageIndex > maxIndex) setPageIndex(maxIndex);
  }, [reportingTotal, pageSize, pageIndex]);

  useEffect(() => {
    if (!companyId) return;

    const fetchData = async () => {
      try {
        await dispatch(
          getReportingData(companyId, { page: pageIndex + 1, pageSize })
        );
      } catch (err) {
        toast({
          title: 'Error fetching data',
          description: err.message,
          status: 'error',
          duration: 5000,
          isClosable: true,
        });
      }
    };
    fetchData();
  }, [dispatch, toast, companyId, pageIndex, pageSize]);

  useEffect(() => {
    const socket = io(`${base_url}`);

    const refetchCurrentPage = () => {
      const p = fetchParamsRef.current;
      if (!p.companyId) return;
      dispatch(
        getReportingData(p.companyId, {
          page: p.pageIndex + 1,
          pageSize: p.pageSize,
          silent: true,
        })
      );
    };

    socket.on('reportUpdate', refetchCurrentPage);

    return () => {
      socket.off('reportUpdate', refetchCurrentPage);
      socket.disconnect();
    };
  }, [dispatch]);

  const columns = useMemo(
    () => [
      { Header: 'Ticket ID', accessor: 'ticket_id' },
      {
        Header: 'Draw ID',
        accessor: 'draw_id',
        Cell: ({ value }) => {
          const today = new Date().toISOString().split('T')[0];
          const userID = localStorage.getItem('username') || '';
          return (
            <a
              href={`drawtickets?drawId=${value}&UserID=${userID}&FDate=${today}&TDate=${today}`}
              style={{ color: 'blue', textDecoration: 'underline' }}
            >
              {value}
            </a>
          );
        },
      },
      {
        Header: 'Draw Time',
        accessor: 'draw_time',
        Cell: ({ value }) => new Date(value).toLocaleString(),
      },
      { Header: 'Total Points', accessor: 'total_points' },
      { Header: 'Win Points', accessor: 'win_points' },
      {
        Header: 'Status',
        accessor: 'status',
        Cell: ({ value }) => (value ? value.charAt(0).toUpperCase() + value.slice(1) : 'Unknown'),
      },
      {
        Header: 'Entry Time',
        accessor: 'entry_time',
        Cell: ({ value }) => new Date(value).toLocaleString(),
      },
    ],
    []
  );

  const visibleColumns = useMemo(
    () => filterColumns('reportAllTicket', columns),
    [columns, filterColumns]
  );

  if (loading && (!reportingData || reportingData.length === 0)) {
    return <Box>Loading...</Box>;
  }
  if (error) return <Box>Error: {error}</Box>;

  return (
    <Box>
      <Heading mb={4}>All Reporting</Heading>
      {isSuperAdmin && (
        <Flex mb={4} gap={4} align="center">
          <CompanySelect value={companyId} onChange={setCompanyId} />
        </Flex>
      )}
      {!companyId ? (
        <Box>{isSuperAdmin ? 'Please select a company to view tickets.' : 'Loading company…'}</Box>
      ) : (
        <GenericTable
          columns={visibleColumns}
          data={Array.isArray(reportingData) ? reportingData : []}
          manualPagination
          pageIndex={pageIndex}
          pageSize={pageSize}
          totalRowCount={reportingTotal ?? 0}
          onPageChange={setPageIndex}
          onPageSizeChange={(size) => {
            setPageSize(size);
            setPageIndex(0);
          }}
          rowKeyAccessor="ticket_id"
          compactOnMobile
        />
      )}
    </Box>
  );
};

export default AllReporting;
